import Expander from '@sats-group/ui-lib/react/expander';
import LinkButton from '@sats-group/ui-lib/react/link-button';
import Text from '@sats-group/ui-lib/react/text';
import React from 'react';
import BootCampComponentList from '~/client/components/boot-camp-component-list/boot-camp-component-list';
import ContentContainer from '~/client/components/content-container/content-container';
import DisplayBootCampEvents from '~/client/components/display-boot-camp-events/display-boot-camp-events';
import DropdownWrapper from '~/client/components/dropdown-wrapper/dropdown-wrapper';
import DynamicImage from '~/client/components/dynamic-image/dynamic-image';
import ElementInterpolator from '~/client/components/element-interpolator/element-interpolator';
import Hero from '~/client/components/hero/hero';
import Layout from '~/client/components/layout/layout';
import PreviewCard from '~/client/components/preview-card/preview-card';
import RichText from '~/client/components/rich-text/rich-text';
import UspList from '~/client/components/usp-list/usp-list';
import type { BootCampLandingPage as Props } from './boot-camp-landing-page.types';

const BootCampLandingPage: React.FunctionComponent<Props> = ({
  allConcepts,
  bootCampConcepts,
  bootCampList,
  description,
  events,
  heading,
  hero,
  layout,
  results,
  usps,
  faqs,
}) => (
  <Layout {...layout}>
    {hero ? (
      <div className="boot-camp-page__hero">
        <Hero {...hero} />
      </div>
    ) : null}
    {usps ? (
      <div className="boot-camp-page__usps">
        <ContentContainer theme={ContentContainer.themes.wide}>
          <UspList {...usps} />
        </ContentContainer>
      </div>
    ) : null}
    <div className="boot-camp-page__content-wrapper">
      <ContentContainer theme={ContentContainer.themes.wide}>
        <Text
          size={Text.sizes.headline2}
          theme={Text.themes.emphasis}
          elementName="h2"
          className="boot-camp-page__concept-heading"
        >
          {heading}
        </Text>
        {description ? (
          <div className="boot-camp-page__concept-description">
            <Text size={Text.sizes.basic}>{description}</Text>
          </div>
        ) : null}
        {bootCampConcepts ? (
          <div className="boot-camp-page__concepts-wrapper">
            {bootCampConcepts.map(bootCampConcept => (
              <a
                key={bootCampConcept.name}
                href={bootCampConcept.url}
                className="boot-camp-page__concept"
              >
                {bootCampConcept.image ? (
                  <DynamicImage
                    className="boot-camp-page__concept-image"
                    {...bootCampConcept.image}
                  />
                ) : null}
                <div className="boot-camp-page__concept-texts">
                  <Text
                    size={Text.sizes.large}
                    elementName="h3"
                    theme={Text.themes.normal}
                    italic
                  >
                    {bootCampConcept.name}
                  </Text>
                  {bootCampConcept.description ? (
                    <Text size={Text.sizes.small}>
                      {bootCampConcept.description}
                    </Text>
                  ) : null}
                </div>
              </a>
            ))}
          </div>
        ) : null}
        {bootCampList ? (
          <BootCampComponentList
            {...bootCampList}
            entryRenderer={PreviewCard}
          />
        ) : null}

        {allConcepts ? (
          <div className="boot-camp-page__all-concepts-button">
            <LinkButton {...allConcepts} />
          </div>
        ) : null}
      </ContentContainer>
      <div className="boot-camp-page__dropdown-wrapper" id={events.id}>
        <ContentContainer theme={ContentContainer.themes.wide}>
          <Text
            className="boot-camp-page__concept-heading"
            elementName="h2"
            size={Text.sizes.headline2}
            theme={Text.themes.emphasis}
          >
            {events.title}
          </Text>
          <DropdownWrapper {...events.dropdown} />
          <Text size={Text.sizes.small} className="boot-camp-page__number">
            <ElementInterpolator
              template={results}
              elements={{
                number: (
                  <Text elementName="span" theme={Text.themes.emphasis}>
                    {events.total}
                  </Text>
                ),
              }}
            />
          </Text>
        </ContentContainer>
        <ContentContainer
          theme={ContentContainer.themes.wide}
          className="boot-camp-page__events"
        >
          <DisplayBootCampEvents {...events.data} />
        </ContentContainer>
      </div>
      {faqs ? (
        <ContentContainer
          theme={ContentContainer.themes.wide}
          className="boot-camp-page__faq-container"
        >
          {faqs.map((faq, index) => (
            <Expander key={index} {...faq} itemRenderer={RichText} />
          ))}
        </ContentContainer>
      ) : null}
    </div>
  </Layout>
);

export default BootCampLandingPage;
